// ---------------------------------------------------------------
// Smart curfew per vehicle. The owner sets a window (start → end time) and which
// weekdays it applies. During the window, the app looks at whether the car is
// HEADING HOME (calm "ETA 11:15" note) or moving AWAY / still out (a real alert).
// Direction matters — a teen driving toward home after curfew is very different
// from one driving away.
//   cfg = { enabled, startTime:'HH:MM', endTime:'HH:MM', days:[0..6],
//           homeLat, homeLng, homeName }
//   days: 0=Sun … 6=Sat. Empty/absent = every day.
// ---------------------------------------------------------------

const KEY = 'dd-curfew';

export const DEFAULT_CURFEW = {
  enabled: false,
  startTime: '23:00',
  endTime: '05:00',
  days: [0, 1, 2, 3, 4, 5, 6],
  homeLat: null, homeLng: null, homeName: '',
};

// migrate the old single-time + grace shape → start/end window
function normalize(cfg) {
  const c = { ...DEFAULT_CURFEW, ...(cfg || {}) };
  if (cfg && cfg.startTime == null && cfg.time) c.startTime = cfg.time; // legacy
  if (!Array.isArray(c.days)) c.days = [...DEFAULT_CURFEW.days];
  return c;
}

export function getCurfew(deviceId) {
  try {
    const all = JSON.parse(localStorage.getItem(KEY)) || {};
    return normalize(all[deviceId]);
  } catch { return { ...DEFAULT_CURFEW }; }
}

export function saveCurfew(deviceId, cfg) {
  let all = {};
  try { all = JSON.parse(localStorage.getItem(KEY)) || {}; } catch { /* ignore */ }
  all[deviceId] = normalize(cfg);
  localStorage.setItem(KEY, JSON.stringify(all));
}

const toMin = (t) => {
  const [h, m] = String(t || '').split(':').map((n) => parseInt(n, 10));
  return isNaN(h) ? null : h * 60 + (m || 0);
};

// Is "now" inside the curfew window, on an enabled weekday? Handles windows that
// cross midnight (e.g. 23:00 → 05:00). Returns { active, nightKey } — nightKey
// identifies the curfew evening so alerts fire once per night.
export function curfewWindow(cfg, now = new Date()) {
  if (!cfg || !cfg.enabled) return { active: false, nightKey: '' };
  const start = toMin(cfg.startTime), end = toMin(cfg.endTime);
  if (start == null || end == null) return { active: false, nightKey: '' };
  const cur = now.getHours() * 60 + now.getMinutes();
  const crosses = end <= start; // window spans midnight
  let inWindow = false;
  const startDate = new Date(now);
  if (!crosses) {
    inWindow = cur >= start && cur < end;
  } else if (cur >= start) {
    inWindow = true; // evening portion, same calendar day
  } else if (cur < end) {
    inWindow = true; // early-morning portion → belongs to the previous day
    startDate.setDate(startDate.getDate() - 1);
  }
  if (!inWindow) return { active: false, nightKey: '' };
  const days = Array.isArray(cfg.days) ? cfg.days : null;
  if (days && days.length && !days.includes(startDate.getDay())) return { active: false, nightKey: '' };
  return { active: true, nightKey: startDate.toISOString().slice(0, 10) };
}

// Bearing (degrees, 0=N) from point A to point B.
export function bearing(aLat, aLng, bLat, bLng) {
  const toR = Math.PI / 180, toD = 180 / Math.PI;
  const dLng = (bLng - aLng) * toR;
  const y = Math.sin(dLng) * Math.cos(bLat * toR);
  const x = Math.cos(aLat * toR) * Math.sin(bLat * toR) - Math.sin(aLat * toR) * Math.cos(bLat * toR) * Math.cos(dLng);
  return (Math.atan2(y, x) * toD + 360) % 360;
}

// Straight-line miles between two points.
export function milesBetween(aLat, aLng, bLat, bLng) {
  const R = 3958.8, toR = Math.PI / 180;
  const dLat = (bLat - aLat) * toR, dLng = (bLng - aLng) * toR;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * toR) * Math.cos(bLat * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
